<script setup lang="ts">
import { storeToRefs } from 'pinia'
import ModalLoadingOverlay from '~/components/app/ModalLoadingOverlay.vue'
import { asyncModalStates } from '~/utils/asyncModalStates'
import { useVisitorStore } from '~/stores/visitor'
import { mountOnceOpened } from '~/utils/mountOnceOpened'
import { whenIdle } from '~/utils/whenIdle'
import { useAuthDeepLink } from '~/composables/useAuthDeepLink'
import { ensureSolanaWalletInitialized } from '~/utils/solanaWalletInit'

// Lazy load LoginModal. It calls solana-wallets-vue's useWallet(), so the
// Solana wallet must be initialized first — do it inside the async loader
// instead of an eager plugin so the Solana stack stays out of first paint.
//
// `defineAsyncComponent` fetches on first RENDER, and this modal is rendered
// unconditionally, so until #855 that whole chain — Solana adapters plus the
// modal itself, 362 KB — was pulled during boot, competing with the render-
// blocking CSS. The template now gates it on `loginMounted`, which opens on
// idle rather than on open, so the bytes leave the critical path but the modal
// is already resolved long before anyone taps Sign In. Gating it purely on
// open was rejected in #819: login is the top of the funnel and a visible wait
// on that tap costs more than the bytes save.
const LoginModal = defineAsyncComponent({
  loader: async () => {
    await ensureSolanaWalletInitialized()
    return import('~/components/auth/LoginModal.vue')
  },
  // Gating it means a tap can now land while the chunk is still in flight, and a
  // failed chunk would otherwise be silent forever — exactly what #845 added
  // these states for. They were skipped here only because this modal used to be
  // ungated.
  ...asyncModalStates(ModalLoadingOverlay, {
    isVisible: () => uiStore.showLoginModal,
    onClose: () => uiStore.closeLoginModal(),
  }),
})

// Deposit and Withdraw are mounted on first open (see mountOnceOpened below),
// so their chunk is fetched inside the user's tap — see utils/asyncModalStates
// for why each of those options is load-bearing.
const DepositModal = defineAsyncComponent({
  loader: () => import('~/components/deposit/DepositModal.vue'),
  ...asyncModalStates(ModalLoadingOverlay, {
    isVisible: () => uiStore.showDepositModal,
    onClose: () => uiStore.closeDepositModal(),
  }),
})

const WithdrawModal = defineAsyncComponent({
  loader: () => import('~/components/withdraw/WithdrawModal.vue'),
  ...asyncModalStates(ModalLoadingOverlay, {
    isVisible: () => uiStore.showWithdrawModal,
    onClose: () => uiStore.closeWithdrawModal(),
  }),
})

// Lazy load VersionUpdateBanner — only mounts on client, prod-relevant only
const VersionUpdateBanner = defineAsyncComponent(
  () => import('~/components/app/VersionUpdateBanner.vue')
)

// Global MFA challenge modal — mounted once, opens whenever any API call
// returns 428 MFA_CHALLENGE_REQUIRED via the withMfa() retry wrapper.
const MfaChallengeModal = defineAsyncComponent(
  () => import('~/components/mfa/MfaChallengeModal.vue')
)

// Analytics facade (auto-imported from layers/base)
const analytics = useAnalytics()

// Initialize stores
const uiStore = useUIStore()
const userStore = useUserStore()
const visitorStore = useVisitorStore()
const { isAuthenticated } = storeToRefs(userStore)

// Deposit and Withdraw share the chunk that carries the LiFi widget's React +
// wallet stack, so they are kept out of the startup module graph entirely and
// mounted on first open — see utils/mountOnceOpened (#819). Login is gated too
// since #855, but on idle rather than on open — see the loader above.
const depositMounted = mountOnceOpened(() => uiStore.showDepositModal)
const withdrawMounted = mountOnceOpened(() => uiStore.showWithdrawModal)

// Game-page Deposit taps land on /wallet before the modal opens (#864); this
// closes it again when the route path moves away (browser Back), and has to
// live here because the modal component itself may still be a chunk in flight.
useDepositModalRouteClose()

// Login is warmed on idle instead of waited for on tap — see the LoginModal
// loader above. `whenIdle` carries a 3 s deadline, so a device that never
// reports an idle period still gets it; and if the user beats that, opening the
// modal flips the same latch immediately, so the worst case is the plain gate.
const loginWarm = ref(false)
const loginMounted = mountOnceOpened(() => loginWarm.value || uiStore.showLoginModal)

// Capture referral code from URL (?ref=CODE) before navigation loses it, and
// record the affiliate click (client-only, fire-and-forget) so Total Clicks /
// EPC / Analytics have data — the share link lands here directly, not via the
// affiliates-track redirect.
const { captureReferralCode, captureAttribution, recordClick } = useReferral()
captureReferralCode()
// Last-touch, ref-aware acquisition attribution for ALL visitors (organic
// included) — captures UTM + document.referrer + landing page even without a
// ?ref=, and lets a newer ad/utm touch supersede an earlier referral. Runs
// after captureReferralCode so a same-visit ?ref= is already stored.
captureAttribution()
recordClick()

// `?reg=1` on any URL opens the login / sign-up modal on arrival (#831), so ad
// and email links can land straight on registration. Instantiated here (setup)
// because it calls useRouter(); fired from onMounted so it never runs on SSR.
const { init: initAuthDeepLink } = useAuthDeepLink()

// Speed Wallet mini-app arrival (`?acct=&lang=&p_add=`): remember the Speed
// account + Lightning address, follow Speed's language and credit the visit to
// the Speed Wallet affiliate (code fetched from the backend config). Same shape
// as the `?reg` deep link — setup here (useLocaleSync is setup-only), fired
// from onMounted. captureAttribution above already counts the arrival as a
// referral touch, so a same-visit utm cannot wipe the code once it lands.
const { capture: captureSpeedWallet } = useSpeedWallet()

// Keep backend session alive while user is on site (silent refresh every 10 min)
if (import.meta.client) {
  useAuthRefresh()
  // Sync users.language with the active UI locale for authenticated users
  // (backend emails / verification read it). Client-only, and invoked HERE — a
  // real component setup — rather than a Nuxt plugin: it goes through
  // useLocaleSync, whose useI18n()/useRoute()/useSwitchLocalePath() are
  // setup-only and throw "Must be called at the top of a `setup` function"
  // from a plugin context.
  useUserLanguageSync()
}

// Trigger Edge Function warmup on app open. Shared composable (layers/base)
// so apps/admin and apps/preview can opt in trivially. SSR-safe + deferred
// via requestIdleCallback so it doesn't compete with hydration-critical
// fetches. Edge Function dedupes via Redis (30s) across all callers.
useWarmupPing()

// Check visitor eligibility on every anonymous page-load.
// IMPORTANT: no *billed* Fingerprint Pro call here — identification still
// defers to the first action (free-bet click / signup / login). Uses the
// visitor_id from localStorage (if any) + request IP server-side via
// /visitor-eligibility.
// Since #819 the store does schedule an unbilled agent warm-up from this path
// when the visitor is free-bet eligible, but only on requestIdleCallback — see
// utils/warmFingerprint.ts for why it must never run inline here.
// See docs/superpowers/specs/2026-05-26-identity-state-machine-design.md.
function checkVisitorEligibility(): void {
  if (isAuthenticated.value) return
  visitorStore.checkEligibility().catch((err: unknown) => {
    console.warn('[app] Visitor eligibility check failed:', err)
  })
}

// On login → clear visitor state (server already transferred balance via auth-login).
// On logout → re-check eligibility so the UI reflects the new anonymous state.
watch(isAuthenticated, (authed) => {
  if (authed) {
    visitorStore.clearVisitor()
  } else {
    checkVisitorEligibility()
  }
})

// Initialize window resize listener + visitor eligibility on client
onMounted(() => {
  whenIdle(() => {
    loginWarm.value = true
  })
  uiStore.initResizeListener()
  checkVisitorEligibility()
  initAuthDeepLink().catch((err: unknown) => {
    console.warn('[app] ?reg deep link failed:', err)
  })
  captureSpeedWallet().catch((err: unknown) => {
    console.warn('[app] Speed Wallet arrival failed:', err)
  })
  analytics.track('app_open', {
    userType: userStore.user?.id ? 'authenticated' : 'visitor',
    deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
  })
})
</script>

<template>
  <div>
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <Toaster />
    <ClientOnly>
      <LoginModal v-if="loginMounted" />
      <DepositModal v-if="depositMounted" />
      <WithdrawModal v-if="withdrawMounted" />
      <MfaChallengeModal />
      <VersionUpdateBanner />
    </ClientOnly>
    <!--
      Recovery-failed UI is mounted INSIDE CrashGraphContainer.vue now —
      it replaces the canvas inline so cashout buttons in the parent
      bet panel stay clickable. Don't re-add a global mount here.
    -->
  </div>
</template>
